import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { Button, CheckBox, Form as DxForm, NumberBox } from 'devextreme-react'
import { forwardRef, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { CustomRule, FormRef as DXFormRef, GroupItem, SimpleItem, Tab, TabbedItem } from 'devextreme-react/form'
import { ValidationCallbackData } from 'devextreme/common'
import { FieldDataChangedEvent } from 'devextreme/ui/form'
import { useMutation, useQuery } from '@tanstack/react-query'
import UserStyles from '@/routes/admin/users/user/User.module.scss'
import { HashLoader } from 'react-spinners'
import RouterErrorHandler from '@/components/RouterErrorHandler'
import { ChildFormHandle } from '@/routes/admin/users/user/forms/types'
import { giorno_lavorativo, info_impiegato } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { ArraySourceComposer } from '@/auth/api/connector'
import { Workday } from '@/enums'
import { CheckBoxTypes } from 'devextreme-react/cjs/check-box'
import { NumberBoxRef } from 'devextreme-react/cjs/number-box'
import {
  FIXED_WORKING_HOURS_MODE,
  FLEXIBLE_SCHEDULE_DAY,
  FLEXIBLE_WORKING_HOURS_MODE,
} from '@/routes/admin/users/user/User.types'
import {
  Label,
  MinorTickInterval,
  Scale,
  SliderMarker,
  TickInterval,
  RangeSelector,
  RangeSelectorRef,
  RangeSelectorTypes,
  MinRange,
} from 'devextreme-react/cjs/range-selector'

export type DetailedUserInfoProps = {
  infoId: number
}

/** Durata minima della pausa pranzo (in minuti) tra l'uscita mattina e l'inizio del pomeriggio. */
const MIN_LUNCH_BREAK_MINUTES = 15

const hasFlexibleWorkingHours = (data: info_impiegato) => {
  return (
    (data.modalita_orario_lavorativo as unknown) === true ||
    data.modalita_orario_lavorativo === FLEXIBLE_WORKING_HOURS_MODE
  )
}

/**
 * Verifica che le ore settimanali siano un multiplo esatto delle ore giornaliere
 * e non superino l'equivalente di 7 giorni lavorativi.
 */
const isWeeklyHoursMultipleOfDaily = (dailyHours: number, weeklyHours: number) => {
  if (dailyHours <= 0) return false
  const equivalentWorkingDays = weeklyHours / dailyHours
  return Math.abs(equivalentWorkingDays - Math.round(equivalentWorkingDays)) < 0.001 && equivalentWorkingDays <= 7
}

const hasValidFlexibleHours = (data: info_impiegato) => {
  if (!hasFlexibleWorkingHours(data)) return true

  const dailyHours = Number(data.giorni_lavorativi?.find((day) => day.giorno === FLEXIBLE_SCHEDULE_DAY)?.ore)
  const weeklyHours = Number(data.ore_settimanali)

  return weeklyHours > 0 && isWeeklyHoursMultipleOfDaily(dailyHours, weeklyHours)
}

const calculateWorkingDayHours = (workingDay: giorno_lavorativo) => {
  const morningHours =
    (new Date(workingDay.uscita_mattina ?? '').getTime() - new Date(workingDay.entrata_mattina ?? '').getTime()) /
    3600000
  const afternoonHours = workingDay.orario_continuato
    ? 0
    : (new Date(workingDay.uscita_pomeriggio ?? '').getTime() -
        new Date(workingDay.entrata_pomeriggio ?? '').getTime()) /
      3600000

  return morningHours + afternoonHours
}

type WorkingDayRowProps = {
  /** Giorno da rappresentare (usato per orari di default, stato "orario continuato" e ore totali). */
  workingDay: giorno_lavorativo
  /** Etichetta del giorno; se assente non viene mostrata la colonna nome (caso giorno flessibile). */
  dayLabel?: string
  disabled: boolean
  /**
   * Se `true` il NumberBox delle ore è controllato (`value`), come nei giorni fissi.
   * Se `false` è non controllato (`defaultValue`) e va aggiornato via `hoursRef`, come nel giorno flessibile:
   * così i drag non forzano un re-render del template e non si generano flash sui RangeSelector.
   */
  hoursControlled: boolean
  hoursRef?: Ref<NumberBoxRef>
  afternoonRangeRef?: Ref<RangeSelectorRef>
  onMorningChange: (event: RangeSelectorTypes.ValueChangedEvent) => void
  onAfternoonChange: (event: RangeSelectorTypes.ValueChangedEvent) => void
  onToggleContinuato: () => void
}

/** Editor di un singolo giorno lavorativo (mattina/pomeriggio + pausa + ore), condiviso tra giorni fissi e flessibile. */
const WorkingDayRow = ({
  workingDay,
  dayLabel,
  disabled,
  hoursControlled,
  hoursRef,
  afternoonRangeRef,
  onMorningChange,
  onAfternoonChange,
  onToggleContinuato,
}: WorkingDayRowProps) => {
  // Larghezza del NumberBox "Ore": prop del widget DevExtreme, non esprimibile via CSS.
  const isCompactLayout = window.innerWidth < 1024
  const hoursProps = hoursControlled ? { value: workingDay.ore } : { defaultValue: workingDay.ore }

  return (
    <div className={UserStyles.workingDayRow}>
      {dayLabel && <div className={UserStyles.workingDayName}>{dayLabel}</div>}

      <div className={UserStyles.workingDayRangesContainer}>
        {/* Morning Range */}
        <div className={UserStyles.workingDayRange}>
          <RangeSelector
            width={'100%'}
            height={'100px'}
            disabled={disabled}
            defaultValue={[new Date(workingDay.entrata_mattina ?? ''), new Date(workingDay.uscita_mattina ?? '')]}
            onValueChanged={onMorningChange}
          >
            <SliderMarker format="shorttime" />
            <Scale startValue={new Date(1970, 0, 1, 7, 0, 0)} endValue={new Date(1970, 0, 1, 20, 0, 0)}>
              <TickInterval hours={1} />
              <MinorTickInterval minutes={15} />
              <Label format="shorttime" />
              <MinRange minutes={15} />
            </Scale>
          </RangeSelector>
        </div>

        {/* Afternoon Range */}
        {!workingDay.orario_continuato && (
          <div className={UserStyles.workingDayRange}>
            <RangeSelector
              ref={afternoonRangeRef}
              width={'100%'}
              height={'100px'}
              disabled={disabled}
              defaultValue={[
                new Date(workingDay.entrata_pomeriggio ?? ''),
                new Date(workingDay.uscita_pomeriggio ?? ''),
              ]}
              onValueChanged={onAfternoonChange}
            >
              <SliderMarker format="shorttime" />
              <Scale
                startValue={new Date(workingDay.uscita_mattina ?? '').setMinutes(
                  new Date(workingDay.uscita_mattina ?? '').getMinutes() + MIN_LUNCH_BREAK_MINUTES,
                )}
                endValue={new Date(1970, 0, 1, 20, 0, 0)}
              >
                <TickInterval hours={1} />
                <MinorTickInterval minutes={15} />
                <Label format="shorttime" />
                <MinRange minutes={15} />
              </Scale>
            </RangeSelector>
          </div>
        )}

        {/* Toggle Button */}
        <div className={UserStyles.workingDayToggle}>
          <Button
            icon={!workingDay.orario_continuato ? 'minus' : 'add'}
            disabled={disabled}
            hint={!workingDay.orario_continuato ? 'Rimuovi Pomeriggio' : 'Aggiungi Pomeriggio'}
            onClick={onToggleContinuato}
          />
        </div>
      </div>

      {/* Total Hours */}
      <div className={UserStyles.workingDayHours}>
        <NumberBox ref={hoursRef} width={isCompactLayout ? '100%' : 100} label="Ore" readOnly={true} {...hoursProps} />
      </div>
    </div>
  )
}

export const DetailedUserInfo = forwardRef<ChildFormHandle, DetailedUserInfoProps>((props, ref) => {
  const days = [
    Workday.Sunday,
    Workday.Monday,
    Workday.Tuesday,
    Workday.Wednesday,
    Workday.Thursday,
    Workday.Friday,
    Workday.Saturday,
  ]

  const [formData, setFormData] = useState<info_impiegato | null>(null)
  const qsadminapiService = useQsAdminApiManager()
  const dxFormRef = useRef<DXFormRef>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const flexibleWorkingDayHoursRef = useRef<NumberBoxRef>(null)
  const flexibleAfternoonRangeRef = useRef<RangeSelectorRef>(null)

  const getCurrentFormData = () => {
    return dxFormRef.current?.instance().option('formData') as info_impiegato | undefined
  }

  const validateCurrentForm = () => {
    const currentFormData = getCurrentFormData()
    const validationResult = dxFormRef.current?.instance().validate()
    return validationResult?.isValid === true && !!currentFormData && hasValidFlexibleHours(currentFormData)
  }

  useImperativeHandle(ref, () => ({
    validate: validateCurrentForm,
    submit: () => {
      formRef.current?.requestSubmit()
    },
  }))

  const fetcher = useQuery({
    queryKey: ['detailedUserInfo', props.infoId],
    queryFn: () => {
      return qsadminapiService.service.info_impiegato(props.infoId).query((builder) => {
        builder.expand('auto')
        builder.expanding('giorni_lavorativi', (giorno_lavorativoBuilder, giornoLavorativoField) => {
          giorno_lavorativoBuilder.orderBy(giornoLavorativoField.giorno.asc())
        })
      })
    },
    refetchOnWindowFocus: false,
  })

  const autoFetcher = useQuery({
    queryKey: ['auto'],
    queryFn: () => {
      return qsadminapiService.service.auto().query()
    },
    refetchOnWindowFocus: false,
  })

  const patcher = useMutation({
    mutationFn: (payload: info_impiegato) => {
      return qsadminapiService.service.info_impiegato(props.infoId).patch(payload)
    },
  })

  // Sync fetcher data to local state
  useEffect(() => {
    if (fetcher.data?.data) {
      const workingDays =
        fetcher.data.data.giorni_lavorativi?.map((day) => ({
          ...day,
          attivo: day.giorno === FLEXIBLE_SCHEDULE_DAY ? false : day.attivo,
        })) ?? []

      setFormData({
        ...fetcher.data.data,
        giorni_lavorativi: workingDays.sort((left, right) => left.giorno - right.giorno),
      })
    }
  }, [fetcher.data])

  if (fetcher.isLoading)
    return (
      <div className={UserStyles.loaderWrapper}>
        <HashLoader loading={fetcher.isLoading} size={100} color={'#ff5722'} />
      </div>
    )
  if (fetcher.error) return <RouterErrorHandler />
  if (!formData) return null

  const flexibleWorkingHours = hasFlexibleWorkingHours(formData)
  const fixedWorkingDays =
    formData.giorni_lavorativi?.filter((day) => day.giorno >= 0 && day.giorno < FLEXIBLE_SCHEDULE_DAY) ?? []
  const flexibleWorkingDay = formData.giorni_lavorativi?.find((day) => day.giorno === FLEXIBLE_SCHEDULE_DAY)

  // Aggiornamento controllato (via setFormData) di un giorno fisso, con ricalcolo delle ore.
  const updateFixedWorkingDay = (index: number, update: (workingDay: giorno_lavorativo) => giorno_lavorativo) => {
    setFormData((prev) => {
      if (!prev?.giorni_lavorativi?.[index]) return prev
      const updated = { ...prev }
      updated.giorni_lavorativi = [...prev.giorni_lavorativi]
      updated.giorni_lavorativi[index] = update(updated.giorni_lavorativi[index])
      return updated
    })
  }

  const updateFlexibleWorkingDayRange = (changes: Partial<giorno_lavorativo>) => {
    const currentFormData = getCurrentFormData() ?? formData
    const currentWorkingDayIndex =
      currentFormData.giorni_lavorativi?.findIndex((workingDay) => workingDay.giorno === FLEXIBLE_SCHEDULE_DAY) ?? -1
    if (currentWorkingDayIndex < 0 || !currentFormData.giorni_lavorativi) return

    const updatedWorkingDay = {
      ...currentFormData.giorni_lavorativi[currentWorkingDayIndex],
      ...changes,
    }
    updatedWorkingDay.ore = calculateWorkingDayHours(updatedWorkingDay)
    currentFormData.giorni_lavorativi[currentWorkingDayIndex] = updatedWorkingDay

    const stateWorkingDayIndex =
      formData.giorni_lavorativi?.findIndex((workingDay) => workingDay.giorno === FLEXIBLE_SCHEDULE_DAY) ?? -1
    if (stateWorkingDayIndex >= 0 && formData.giorni_lavorativi) {
      formData.giorni_lavorativi[stateWorkingDayIndex] = updatedWorkingDay
    }

    flexibleWorkingDayHoursRef.current?.instance().option('value', updatedWorkingDay.ore)

    if (changes.uscita_mattina) {
      const afternoonStart = new Date(changes.uscita_mattina)
      afternoonStart.setMinutes(afternoonStart.getMinutes() + MIN_LUNCH_BREAK_MINUTES)
      flexibleAfternoonRangeRef.current?.instance().option('scale.startValue', afternoonStart)
    }
  }

  const updateFlexibleWorkingDay = (update: (workingDay: giorno_lavorativo) => giorno_lavorativo) => {
    setFormData((previous) => {
      if (!previous?.giorni_lavorativi) return previous

      return {
        ...previous,
        giorni_lavorativi: previous.giorni_lavorativi.map((workingDay) =>
          workingDay.giorno === FLEXIBLE_SCHEDULE_DAY ? update(workingDay) : workingDay,
        ),
      }
    })
  }

  const handleFieldDataChanged = (event: FieldDataChangedEvent) => {
    if (event.dataField !== 'modalita_orario_lavorativo') return

    const flexibleModeEnabled = Boolean(event.value)

    setFormData((previous) => {
      if (!previous) return previous

      const activeWorkingDays =
        previous.giorni_lavorativi?.filter(
          (day) => day.giorno >= 0 && day.giorno < FLEXIBLE_SCHEDULE_DAY && day.attivo,
        ) ?? []
      const defaultWeeklyHours = Math.round(activeWorkingDays.reduce((total, day) => total + day.ore, 0))

      return {
        ...previous,
        modalita_orario_lavorativo: flexibleModeEnabled ? FLEXIBLE_WORKING_HOURS_MODE : FIXED_WORKING_HOURS_MODE,
        ore_settimanali:
          flexibleModeEnabled && previous.ore_settimanali <= 0 ? defaultWeeklyHours : previous.ore_settimanali,
      }
    })
  }

  return (
    <form
      ref={formRef}
      onSubmit={(e) => {
        e.preventDefault()
        const currentFormData = getCurrentFormData()
        if (!currentFormData || !validateCurrentForm()) return

        patcher.mutate({
          ...currentFormData,
          modalita_orario_lavorativo: hasFlexibleWorkingHours(currentFormData)
            ? FLEXIBLE_WORKING_HOURS_MODE
            : FIXED_WORKING_HOURS_MODE,
        })
      }}
    >
      <DxForm
        ref={dxFormRef}
        formData={formData}
        showValidationSummary={true}
        onFieldDataChanged={handleFieldDataChanged}
      >
        <TabbedItem>
          <Tab title="Dati Aziendali Utente">
            <GroupItem colSpan={4} colCount={5}>
              <SimpleItem
                dataField={'id'}
                colSpan={1}
                label={{
                  text: 'ID',
                }}
                editorOptions={{
                  disabled: true,
                }}
              />
              <SimpleItem
                colSpan={2}
                dataField={'interno'}
                editorType={'dxNumberBox'}
                editorOptions={{
                  min: 0,
                  max: 9999,
                  showSpinButtons: true,
                }}
                label={{
                  text: 'Interno',
                }}
              />
              <SimpleItem
                colSpan={2}
                dataField={'inps'}
                label={{
                  text: 'INPS',
                }}
              />
            </GroupItem>
            <GroupItem colSpan={4} colCount={2}>
              <SimpleItem
                dataField={'email_rec'}
                label={{
                  text: 'Email REC',
                }}
              />
              <SimpleItem
                dataField={'cellulare'}
                label={{
                  text: 'Cellulare',
                }}
              />
            </GroupItem>
            <GroupItem colSpan={4} colCount={2}>
              <SimpleItem
                dataField="badge"
                label={{ text: 'Badge' }}
                editorType="dxSelectBox"
                editorOptions={{
                  dataSource: [true, false],
                  displayExpr: (item: boolean | null) => {
                    if (item === true) return 'SI'
                    else if (item === false) return 'NO'
                    else return null
                  },
                  placeholder: 'Seleziona flag...',
                  searchEnabled: true,
                  dropDownOptions: {
                    showTitle: true,
                    title: 'Selezionare flag',
                    hideOnOutsideClick: true,
                  },
                }}
              ></SimpleItem>
              {autoFetcher.data?.data && (
                <SimpleItem
                  dataField="auto"
                  editorType="dxLookup"
                  editorOptions={{
                    dataSource: ArraySourceComposer('id', autoFetcher.data?.data?.value),
                    placeholder: "Seleziona l'auto...",
                    displayExpr: 'modello',
                    searchEnabled: true,
                    dropDownCentered: true,
                    showClearButton: true,
                    dropDownOptions: {
                      showTitle: true,
                      title: "Selezionare l'auto",
                      hideOnOutsideClick: true,
                    },
                  }}
                />
              )}
            </GroupItem>

            <GroupItem colSpan={4} colCount={2}>
              <SimpleItem
                dataField={'matricola'}
                label={{
                  text: 'Matricola',
                }}
              />
              <SimpleItem
                dataField={'divisione'}
                label={{
                  text: 'Divisione',
                }}
              />
            </GroupItem>
          </Tab>
          <Tab title="Giorni Lavorativi">
            <GroupItem colSpan={4} colCount={1}>
              <SimpleItem
                dataField="modalita_orario_lavorativo"
                editorType="dxSwitch"
                editorOptions={{
                  width: 160,
                  switchedOnText: 'Giorni flessibili',
                  switchedOffText: 'Giorni fissi',
                  elementAttr: {
                    class: UserStyles.workingHoursModeSwitch,
                  },
                }}
                label={{ text: 'Modalità orario lavorativo' }}
              />
            </GroupItem>
            {fixedWorkingDays.length > 0 && (
              <GroupItem colSpan={4} colCount={7} caption={'Giorni fissi'}>
                <SimpleItem
                  colSpan={7}
                  render={() => {
                    return (
                      <div style={{ width: '100%', maxWidth: '100%', overflow: 'visible' }}>
                        <br />
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '10px',
                            marginBottom: '15px',
                          }}
                        >
                          {days.map((day, index) => {
                            const workingDayIndex =
                              formData.giorni_lavorativi?.findIndex((workingDay) => workingDay.giorno === index) ?? -1
                            const workingDay = formData.giorni_lavorativi?.[workingDayIndex]
                            if (!workingDay) return null

                            return (
                              <CheckBox
                                key={index}
                                text={day.substring(0, 3)}
                                disabled={flexibleWorkingHours}
                                value={workingDay.attivo}
                                onValueChanged={(e: CheckBoxTypes.ValueChangedEvent) => {
                                  if (e.value === e.previousValue) return
                                  if (!formData?.giorni_lavorativi) return

                                  setFormData((prev) => {
                                    if (!prev) return prev
                                    const updated = { ...prev }
                                    updated.giorni_lavorativi = [...prev.giorni_lavorativi!]
                                    updated.giorni_lavorativi[workingDayIndex] = {
                                      ...updated.giorni_lavorativi[workingDayIndex],
                                      attivo: e.value,
                                    }
                                    return updated
                                  })
                                }}
                              />
                            )
                          })}
                        </div>
                        {fixedWorkingDays.map((giorno) => {
                          const index =
                            formData.giorni_lavorativi?.findIndex(
                              (workingDay) => workingDay.giorno === giorno.giorno,
                            ) ?? -1

                          if (index < 0) return null
                          if (!giorno.attivo) return null

                          return (
                            <WorkingDayRow
                              key={giorno.id || giorno.giorno}
                              workingDay={giorno}
                              dayLabel={days[giorno.giorno].substring(0, 3)}
                              disabled={flexibleWorkingHours}
                              hoursControlled={true}
                              onMorningChange={(event) =>
                                updateFixedWorkingDay(index, (day) => {
                                  const updatedDay = {
                                    ...day,
                                    entrata_mattina: new Date(event.value[0]).toISOString(),
                                    uscita_mattina: new Date(event.value[1]).toISOString(),
                                  }
                                  return { ...updatedDay, ore: calculateWorkingDayHours(updatedDay) }
                                })
                              }
                              onAfternoonChange={(event) =>
                                updateFixedWorkingDay(index, (day) => {
                                  const updatedDay = {
                                    ...day,
                                    entrata_pomeriggio: new Date(event.value[0]).toISOString(),
                                    uscita_pomeriggio: new Date(event.value[1]).toISOString(),
                                  }
                                  return { ...updatedDay, ore: calculateWorkingDayHours(updatedDay) }
                                })
                              }
                              onToggleContinuato={() =>
                                updateFixedWorkingDay(index, (day) => {
                                  const updatedDay = { ...day, orario_continuato: !day.orario_continuato }
                                  return { ...updatedDay, ore: calculateWorkingDayHours(updatedDay) }
                                })
                              }
                            />
                          )
                        })}
                      </div>
                    )
                  }}
                />
              </GroupItem>
            )}
            <GroupItem colSpan={4} colCount={1} caption={'Giorni flessibili'}>
              <SimpleItem
                dataField="ore_settimanali"
                editorType="dxNumberBox"
                editorOptions={{
                  min: 1,
                  max: 168,
                  disabled: !flexibleWorkingHours,
                  showSpinButtons: true,
                }}
                label={{ text: 'Ore settimanali' }}
              >
                <CustomRule
                  reevaluate={true}
                  message="Le ore settimanali devono essere maggiori di zero"
                  validationCallback={({ value }: ValidationCallbackData) =>
                    !flexibleWorkingHours || (typeof value === 'number' && value > 0)
                  }
                />
                <CustomRule
                  reevaluate={true}
                  message="Le ore settimanali devono essere un multiplo delle ore giornaliere e corrispondere al massimo a 7 giorni"
                  validationCallback={({ value }: ValidationCallbackData) => {
                    if (!flexibleWorkingHours) return true
                    return (
                      typeof value === 'number' && isWeeklyHoursMultipleOfDaily(Number(flexibleWorkingDay?.ore), value)
                    )
                  }}
                />
              </SimpleItem>
              <SimpleItem
                colSpan={2}
                render={() =>
                  flexibleWorkingDay && (
                    <WorkingDayRow
                      workingDay={flexibleWorkingDay}
                      disabled={!flexibleWorkingHours}
                      hoursControlled={false}
                      hoursRef={flexibleWorkingDayHoursRef}
                      afternoonRangeRef={flexibleAfternoonRangeRef}
                      onMorningChange={(event) =>
                        updateFlexibleWorkingDayRange({
                          entrata_mattina: new Date(event.value[0]).toISOString(),
                          uscita_mattina: new Date(event.value[1]).toISOString(),
                        })
                      }
                      onAfternoonChange={(event) =>
                        updateFlexibleWorkingDayRange({
                          entrata_pomeriggio: new Date(event.value[0]).toISOString(),
                          uscita_pomeriggio: new Date(event.value[1]).toISOString(),
                        })
                      }
                      onToggleContinuato={() =>
                        updateFlexibleWorkingDay((workingDay) => {
                          const updatedWorkingDay = { ...workingDay, orario_continuato: !workingDay.orario_continuato }
                          return { ...updatedWorkingDay, ore: calculateWorkingDayHours(updatedWorkingDay) }
                        })
                      }
                    />
                  )
                }
              />
            </GroupItem>
          </Tab>
        </TabbedItem>
      </DxForm>
    </form>
  )
})

DetailedUserInfo.displayName = 'DetailedUserInfo'
